import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import he from 'date-fns/locale/he';
import { fetchInvoices } from '../utils/requests/invoices';
import { useAtom } from 'jotai';
import { invoiceDateRangeAtom } from '../utils/atoms';
import ReportModal from './ReportModal';

// Register Hebrew locale
registerLocale('he', he);

// Custom styles for the date picker
const datePickerStyles = `
  .react-datepicker-wrapper {
    display: inline-block;
  }
  .react-datepicker__input-container input {
    padding-right: 35px !important;
    text-align: center;
  }
  .react-datepicker {
    font-family: inherit;
    border-radius: 8px;
    border: 1px solid var(--border-color);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  .react-datepicker__header {
    background-color: var(--primary-600);
    color: white;
    border-radius: 8px 8px 0 0;
  }
  .react-datepicker__current-month {
    color: white;
    font-weight: 600;
  }
  .react-datepicker__day-name {
    color: white;
  }
  .react-datepicker__day--selected {
    background-color: var(--primary-600) !important;
    color: white !important;
  }
  .react-datepicker__day--in-range {
    background-color: var(--primary-50) !important;
    color: var(--primary-600) !important;
  }
  .react-datepicker__day--keyboard-selected {
    background-color: var(--primary-100) !important;
    color: var(--primary-600) !important;
  }
  .react-datepicker__day:hover {
    background-color: var(--primary-100) !important;
  }
`;

// Document type mapping
const documentTypeMap = {
    3: { en: "Invoice Receipt", he: "חשבונית מס קבלה" },
    1: { en: "Invoice", he: "חשבונית מס" },
    2: { en: "Receipt", he: "קבלה" },
    4: { en: "Invoice Deal", he: "חשבונית עסקה" },
    5: { en: "Order", he: "הזמנה" },
    8: { en: "Credit", he: "זיכוי" },
    9: { en: "Receipt Credit", he: "קבלה זיכוי" },
    10: { en: "Expense", he: "הוצאה" },
};

const InvoiceListLayer = () => {
    const [invoices, setInvoices] = useState({ documents: [], expenses: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter] = useState('all');
    const [dateRange, setDateRange] = useAtom(invoiceDateRangeAtom);
    const [startDate, setStartDate] = useState(dateRange[0] || new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [endDate, setEndDate] = useState(dateRange[1] || new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
    const [showReportModal, setShowReportModal] = useState(false);

    useEffect(() => {
        // Update the atom when local state changes
        if (startDate && endDate) {
            setDateRange([startDate, endDate]);
        }
    }, [startDate, endDate, setDateRange]);

    useEffect(() => {
        loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    const loadInvoices = async () => {
        try {
            setLoading(true);

            // Validate dates before making API call
            if (!startDate || !endDate) {
                console.log('Dates not ready yet:', { startDate, endDate });
                setLoading(false);
                return;
            }

            const startDateNum = startDate.getTime();
            const endDateNum = endDate.getTime();

            console.log('Loading invoices for dates:', { startDate, endDate, startDateNum, endDateNum });

            const data = await fetchInvoices(startDateNum, endDateNum);
            setInvoices(data);
        } catch (err) {
            setError('Failed to load invoices');
            console.error('Error loading invoices:', err);
        } finally {
            setLoading(false);
        }
    };

    // Combine documents and expenses for display
    const allItems = [
        ...(invoices?.documents ?? []).map(doc => ({ ...doc, type: 'document' })),
        ...(invoices?.expenses ?? []).map(exp => ({ ...exp, type: 'expense' }))
    ];

    const getTimeNumber = (dateString) => {
        if (!dateString) return 0;
        if (String(dateString).includes('/Date(')) {
            const firstPartOff = dateString.replace('/Date(', '')
            const timeAsString = firstPartOff.split(')')[0]
            return Number(timeAsString);
        }
        return 0;
    };
    // Filter items based on search and status
    const filteredItems = allItems.sort((a, b) => getTimeNumber(b.DateCreated) - getTimeNumber(a.DateCreated)).filter(item => {
        const matchesSearch = !searchTerm ||
            item.Title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.ClientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.SupplierName?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'paid' && item.Status === 1) ||
            (statusFilter === 'pending' && item.Status === 0);

        return matchesSearch && matchesStatus;
    });

    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        if (String(dateString).includes('/Date(')) {
            const firstPartOff = dateString.replace('/Date(', '')
            const timeAsString = firstPartOff.split(')')[0]
            console.log(timeAsString);
            return new Date(Number(timeAsString)).toLocaleDateString('he-IL')
        }
        const date = new Date(dateString);
        return date.toLocaleDateString('he-IL');
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₪0.00';
        return `₪${parseFloat(amount).toFixed(2).toLocaleString('he-IL')}`;
    };

    const setQuickDateRange = (type) => {
        const today = new Date();
        let newStartDate, newEndDate;

        switch (type) {
            case 'currentMonth':
                newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
                newEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            case 'lastMonth':
                newStartDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                newEndDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case 'currentYear':
                newStartDate = new Date(today.getFullYear(), 0, 1);
                newEndDate = new Date(today.getFullYear(), 11, 31);
                break;
            case 'prevBiMonth': {
                // Israeli reporting periods: Jan-Feb, Mar-Apr, May-Jun, Jul-Aug, Sep-Oct, Nov-Dec
                const month = today.getMonth(); // 0-based
                const year = today.getFullYear();
                let periodStartMonth, periodEndMonth, periodYear = year;
                if (month < 2) { // Jan or Feb: previous period is Nov-Dec of last year
                    periodStartMonth = 10; // Nov
                    periodEndMonth = 11; // Dec
                    periodYear = year - 1;
                } else if (month < 4) { // Mar or Apr: previous period is Jan-Feb
                    periodStartMonth = 0;
                    periodEndMonth = 1;
                } else if (month < 6) { // May or Jun: previous period is Mar-Apr
                    periodStartMonth = 2;
                    periodEndMonth = 3;
                } else if (month < 8) { // Jul or Aug: previous period is May-Jun
                    periodStartMonth = 4;
                    periodEndMonth = 5;
                } else if (month < 10) { // Sep or Oct: previous period is Jul-Aug
                    periodStartMonth = 6;
                    periodEndMonth = 7;
                } else { // Nov or Dec: previous period is Sep-Oct
                    periodStartMonth = 8;
                    periodEndMonth = 9;
                }
                newStartDate = new Date(periodYear, periodStartMonth, 1);
                newEndDate = new Date(periodYear, periodEndMonth + 1, 0);
                break;
            }
            default:
                return;
        }

        setStartDate(newStartDate);
        setEndDate(newEndDate);
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-body text-center py-40">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">טוען מסמכים...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card">
                <div className="card-body text-center py-40">
                    <Icon icon="mdi:alert-circle" className="text-danger text-4xl mb-3" />
                    <p className="text-danger">{error}</p>
                    <button className="btn btn-primary mt-3" onClick={loadInvoices}>
                        נסה שוב
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{datePickerStyles}</style>
            <div className="card">
                <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex flex-wrap align-items-center gap-3">
                        <div className="icon-field">
                            <input
                                type="text"
                                name="search"
                                className="form-control form-control-sm w-auto"
                                placeholder="חיפוש..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <span className="icon">
                                <Icon icon="ion:search-outline" />
                            </span>
                        </div>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-3">
                        {/* <Link to="/invoice-add" className="btn btn-sm btn-primary-600">
                            <i className="ri-add-line" /> צור חשבונית
                        </Link> */}
                        <button className="btn btn-sm btn-outline-primary" onClick={() => setShowReportModal(true)}>
                            <Icon icon="mdi:download" className="me-1 mb-2" /> הורדת דו״ח
                        </button>
                    </div>
                </div>
                {/* Date range controls below filters */}
                <div className="card-header d-flex flex-wrap align-items-center gap-3 pt-20 pb-3 border-0 bg-transparent">
                    <span className="text-sm fw-medium">טווח תאריכים:</span>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setQuickDateRange('currentMonth')}
                        >
                            החודש
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setQuickDateRange('lastMonth')}
                        >
                            החודש שעבר
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setQuickDateRange('currentYear')}
                        >
                            השנה
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setQuickDateRange('prevBiMonth')}
                        >
                            תקופת דיווח קודמת
                        </button>
                        <div className="d-flex align-items-center gap-2">
                            <div className="position-relative">
                                <DatePicker
                                    selected={startDate}
                                    onChange={date => {
                                        setStartDate(date);
                                        if (date && endDate && date > endDate) {
                                            setEndDate(date);
                                        }
                                    }}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    className="form-control form-control-sm"
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="תאריך התחלה"
                                    locale="he"
                                    minDate={new Date(2020, 0, 1)}
                                    maxDate={endDate || new Date(2030, 11, 31)}
                                />
                                <span className="position-absolute end-0 top-50 translate-middle-y me-8">
                                    <Icon icon="solar:calendar-linear" className="text-secondary-light" />
                                </span>
                            </div>
                            <span>עד</span>
                            <div className="position-relative">
                                <DatePicker
                                    selected={endDate}
                                    onChange={date => {
                                        setEndDate(date);
                                        if (date && startDate && date < startDate) {
                                            setStartDate(date);
                                        }
                                    }}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    className="form-control form-control-sm"
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="תאריך סיום"
                                    locale="he"
                                    minDate={startDate || new Date(2020, 0, 1)}
                                    maxDate={new Date(2030, 11, 31)}
                                />
                                <span className="position-absolute end-0 top-50 translate-middle-y me-8">
                                    <Icon icon="solar:calendar-linear" className="text-secondary-light" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    <table className="table bordered-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col">מס'</th>
                                <th scope="col">סוג</th>
                                <th scope="col">כותרת</th>
                                <th scope="col">לקוח/ספק</th>
                                <th scope="col">תאריך</th>
                                <th scope="col">סכום</th>
                                <th scope="col">פעולות</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-40">
                                        <Icon icon="mdi:file-document-outline" className="text-muted text-3xl mb-2" />
                                        <p className="text-muted">לא נמצאו מסמכים</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredItems.map((item, index) => (
                                    <tr key={item.ID || item.id}>
                                        <td>{item.Number || index + 1}</td>
                                        <td>
                                            {item.type === 'document' ? (
                                                <span className="badge bg-primary">
                                                    {documentTypeMap[item.DocType]?.he || 'מסמך'}
                                                </span>
                                            ) : (
                                                <span className="badge bg-info">הוצאה</span>
                                            )}
                                        </td>
                                        <td>
                                            <Link to="#" className="text-primary-600 fw-medium">
                                                {item.Title || item.title || 'ללא כותרת'}
                                            </Link>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center justify-content-center">
                                                <h6 className="text-md mb-0 fw-medium">
                                                    {item.ClientName || item.SupplierName || item.vendorName || 'ללא שם'}
                                                </h6>
                                            </div>
                                        </td>
                                        <td>{formatDate(item.DateCreated || item.date)}</td>
                                        <td className="fw-medium">{formatCurrency(item.Total || item.total)}</td>
                                        <td>
                                            {item.type === 'document' ? (
                                                <Link
                                                    to={item.documentUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                                                    title="הורדה"
                                                >
                                                    <Icon icon="solar:download-linear" />
                                                </Link>
                                            ) : (
                                                item.attachmentUrl ? (
                                                    <Link
                                                        to={item.attachmentUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                                                        title="הורדה"
                                                    >
                                                        <Icon icon="solar:download-linear" />
                                                    </Link>
                                                ) : (
                                                    <span
                                                        className="w-32-px h-32-px me-8 bg-secondary-light text-secondary-light rounded-circle d-inline-flex align-items-center justify-content-center"
                                                        title="לא צורף קובץ"
                                                    >
                                                        <Icon icon="mdi:file-remove" />
                                                    </span>
                                                )
                                            )}

                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    {filteredItems.length > 0 && (
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-24">
                            <span>מציג {filteredItems.length} מתוך {allItems.length} מסמכים</span>
                            <span className="text-secondary-light text-sm">
                                {formatDate(startDate)} - {formatDate(endDate)}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            {showReportModal && (
                <ReportModal
                    show={showReportModal}
                    onClose={() => setShowReportModal(false)}
                    startDate={startDate}
                    endDate={endDate}
                />
            )}
        </>
    );
};

export default InvoiceListLayer;